import React from 'react';
import {
	ExperienceStart,
	ExperienceSuccess,
	useExperienceFail,
} from '@atlassian/jira-experience-tracker';
import { JIRA_SOFTWARE } from '@atlassian/jira-shared-types';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller';
import { NonCriticalErrorBoundary } from '../../common/utils/non-critical-error-boundary';
import { useProjectAnalyticsAttributes } from '../get-project';

const viewCycleTimeReportExperienceName = 'viewCycleTimeReport';

/**
 * This component should be rendered as soon as we know the user intends to view the
 * Jira Software Report->Cycle time page.
 */
export const ViewCycleTimeReportExperienceStart = ({
	experienceId,
}: {
	experienceId: string | null;
}) => {
	const appEditions = useAppEditions();
	return (
		<ExperienceStart
			experienceId={experienceId}
			experience={viewCycleTimeReportExperienceName}
			analyticsSource="cycleTimeReport"
			edition={appEditions.software}
			application={JIRA_SOFTWARE}
		/>
	);
};

type Attributes = {
	readonly [key: string]: string | number | boolean | null;
};

type OnExperienceFail = (location: string, error: Error, additionalAttributes?: Attributes) => void;

/**
 * The function returned by this hook should be called if an unexpected
 * error occurs, e.g. a JavaScript exception bubbles out of the cycle time report view.
 */
export const useViewCycleTimeReportExperienceFail = (): OnExperienceFail =>
	useExperienceFail({
		experience: viewCycleTimeReportExperienceName,
	});

/**
 * This component should be rendered in "expected error" scenarios, e.g. the
 * user does not have the correct permissions or application license to view
 * the cycle time report page.
 */
type ExpectedErrorSuccessProps = {
	reason: string;
};

export const ViewCycleTimeReportExpectedErrorExperienceSuccessTracker = ({
	reason,
}: ExpectedErrorSuccessProps) => (
	<ExperienceSuccess
		experience={viewCycleTimeReportExperienceName}
		attributes={{
			reason,
		}}
	/>
);

export const ViewCycleTimeReportExperienceSuccessTracker = () => {
	const projectInfo = useProjectAnalyticsAttributes();

	if (projectInfo == null) {
		return null;
	}

	return (
		<NonCriticalErrorBoundary
			fallback="unmount"
			eventLocation="viewCycleTimeReportExperienceSuccessTracker"
		>
			<ExperienceSuccess
				experience={viewCycleTimeReportExperienceName}
				attributes={{
					...projectInfo,
				}}
			/>
		</NonCriticalErrorBoundary>
	);
};
